import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SiteContentProvider } from './contexts/SiteContentContext';
import TopBar from './components/TopBar';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DesignerBadge from './components/DesignerBadge';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Resources from './pages/Resources';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import Contact from './pages/Contact';
import CategoryPage from './pages/CategoryPage';
import ServicePage from './pages/ServicePage';
import CustomPage from './pages/CustomPage';
import BookDetail from './pages/BookDetail';
import AdminLogin from './pages/AdminLogin';
import LegalPage from './pages/LegalPage';
import AdminDashboard from './pages/AdminDashboard';
import StudentCabinet from './pages/StudentCabinet';
import '@/App.css';

// Scroll: preserve on F5, reset on navigation
function ScrollRestoration() {
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);
  const isNavigating = useRef(false);

  useEffect(() => {
    // Continuously save scroll position (debounced) - more reliable than beforeunload
    let timer;
    const saveScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        sessionStorage.setItem('scrollPos_' + window.location.pathname, String(window.scrollY));
      }, 100);
    };
    window.addEventListener('scroll', saveScroll, { passive: true });
    // Also save on beforeunload as backup
    const saveOnUnload = () => {
      sessionStorage.setItem('scrollPos_' + window.location.pathname, String(window.scrollY));
    };
    window.addEventListener('beforeunload', saveOnUnload);
    return () => {
      window.removeEventListener('scroll', saveScroll);
      window.removeEventListener('beforeunload', saveOnUnload);
      clearTimeout(timer);
    };
  }, [pathname]);

  useEffect(() => {
    if (prevPath.current !== pathname) {
      // Navigation → scroll to top
      isNavigating.current = true;
      prevPath.current = pathname;
      sessionStorage.removeItem('scrollPos_' + pathname);
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } else {
      // Same page (refresh) → restore saved position
      isNavigating.current = false;
      const saved = sessionStorage.getItem('scrollPos_' + pathname);
      if (saved && parseInt(saved) > 0) {
        const target = parseInt(saved);
        let attempts = 0;
        const tryRestore = () => {
          attempts++;
          if (attempts > 30) return;
          if (document.body.scrollHeight >= target + 100) {
            window.scrollTo({ top: target, left: 0, behavior: 'instant' });
          } else {
            requestAnimationFrame(() => setTimeout(tryRestore, 100));
          }
        };
        // Start trying immediately + after delays
        tryRestore();
        setTimeout(tryRestore, 300);
        setTimeout(tryRestore, 600);
        setTimeout(tryRestore, 1000);
        setTimeout(tryRestore, 2000);
      }
    }
  }, [pathname]);

  return null;
}

function AppLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isStudent = location.pathname.startsWith('/student');
  const showChrome = !isAdmin && !isStudent;

  return (
    <>
      <ScrollRestoration />
      {showChrome && <TopBar />}
      {showChrome && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/category/:id" element={<CategoryPage />} />
        <Route path="/service/:id" element={<ServicePage />} />
        <Route path="/page/:pageId" element={<CustomPage />} />
        <Route path="/book/:pageId/:bookIndex" element={<BookDetail />} />
        <Route path="/legal/:type" element={<LegalPage />} />
        <Route path="/quiz" element={<Navigate to="/student" replace />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/student" element={<StudentCabinet />} />
        <Route path="/:slug/:bookSlug" element={<BookDetail />} />
        <Route path="/:slug" element={<CustomPage />} />
      </Routes>
      {showChrome && <Footer />}
      {showChrome && <ScrollToTop />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteContentProvider>
          <AppLayout />
        </SiteContentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
